
import React from "react";
import { useTranslation } from "react-i18next";
import { 
  ArrowUpDown, 
  ChevronLeft, 
  ChevronRight, 
  MessageSquare, 
  Send,
  Eye, 
  Edit, 
  Trash 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SocialGroupType } from "@/types/social-groups";

interface SocialGroupsListProps {
  currentItems: SocialGroupType[];
  isRTL: boolean;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  openViewDialog: (group: SocialGroupType) => void;
  openEditDialog: (group: SocialGroupType) => void;
  openDeleteDialog: (group: SocialGroupType) => void;
}

export const SocialGroupsList: React.FC<SocialGroupsListProps> = ({
  currentItems,
  isRTL,
  currentPage,
  totalPages,
  setCurrentPage,
  openViewDialog,
  openEditDialog,
  openDeleteDialog
}) => {
  return (
    <div className="w-full">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b">
              <th className="py-3 px-4 text-start">
                <div className="flex items-center gap-1">
                  <span>{isRTL ? "الاسم" : "Name"}</span>
                  <ArrowUpDown className="h-4 w-4" />
                </div>
              </th>
              <th className="py-3 px-4 text-start">
                <div className="flex items-center gap-1">
                  <span>{isRTL ? "النوع" : "Type"}</span>
                </div>
              </th>
              <th className="py-3 px-4 text-center">
                <div className="flex items-center justify-center gap-1">
                  <span>{isRTL ? "عدد الأعضاء" : "Members"}</span>
                </div>
              </th>
              <th className="py-3 px-4 text-end">
                <span>{isRTL ? "إجراءات" : "Actions"}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((group) => (
                <tr key={group.id} className="border-b hover:bg-muted/50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {group.image_url ? (
                        <img 
                          src={group.image_url} 
                          alt={group.name[isRTL ? "ar" : "en"]}
                          className="w-10 h-10 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                          group.type === "whatsapp" ? "bg-green-100" : "bg-blue-100"
                        }`}>
                          {group.type === "whatsapp" ? (
                            <MessageSquare size={18} className="text-green-600" />
                          ) : (
                            <Send size={18} className="text-blue-600" />
                          )}
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{group.name[isRTL ? "ar" : "en"]}</div>
                        <div className="text-sm text-muted-foreground truncate max-w-[300px]">
                          {group.description[isRTL ? "ar" : "en"]}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {group.type === "whatsapp" ? (
                        <>
                          <MessageSquare size={16} className="text-green-500" />
                          <span>{isRTL ? "واتساب" : "WhatsApp"}</span>
                        </>
                      ) : (
                        <>
                          <Send size={16} className="text-blue-500" />
                          <span>{isRTL ? "تلجرام" : "Telegram"}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {group.members.toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openViewDialog(group)}
                        title={isRTL ? "عرض" : "View"}
                      >
                        <Eye size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(group)}
                        title={isRTL ? "تعديل" : "Edit"}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(group)}
                        title={isRTL ? "حذف" : "Delete"}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="py-10 text-center text-muted-foreground">
                  {isRTL ? "لا توجد مجموعات متطابقة مع بحثك" : "No groups matching your search"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} />
          </Button>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "outline"}
                size="icon"
                onClick={() => setCurrentPage(page)}
                className="w-8 h-8"
              >
                {page}
              </Button>
            ))}
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      )}
    </div>
  );
};
